import { useMemo } from 'react'

import { LEGAL_LINKS, NAVIGATION_LINKS } from '@/lib/definitions/constants'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import { useInsightsOpensExternally } from '@/lib/hooks/useInsightsNavTarget'
import { useAuth } from '@/lib/provider/AuthProvider'
import { useTenant } from '@/lib/provider/TenantProvider'
import type { LinkProps } from '@/layouts/partials/DropdownMenu'
import {
  KNOWLEDGE_BASE_HOME,
  openKnowledgeBase,
} from '@/lib/misc/knowledgeBase'

export interface MainNavItem {
  id: string
  title: string
  to?: string
  external?: boolean
  isHidden?: boolean
  isActive?: boolean
  showArrow?: boolean
  links?: LinkProps[]
  alignment?: 'left' | 'right'
}

/**
 * Single source of truth for the primary destinations, shared by the sidebar
 * and the fallback top toolbar so the two can never drift.
 */
export type KnowledgeBaseAwareLink = LinkProps & { knowledgeBaseKey?: string }

export const withKnowledgeBase = (
  isEnabled: boolean,
  links: KnowledgeBaseAwareLink[]
): LinkProps[] =>
  links.map(({ knowledgeBaseKey, ...link }) =>
    isEnabled && knowledgeBaseKey != null
      ? {
          ...link,
          to: undefined,
          external: false,
          onClick: () => openKnowledgeBase(knowledgeBaseKey || undefined),
        }
      : link
  )

export const useMainNavItems = (): {
  items: MainNavItem[]
  resourceLinks: LinkProps[]
  settingsHref: string
} => {
  const [auth] = useAuth()
  const { customerId } = useTenant()
  const isBenocsEnabled = useFeatureFlag(FLAGS.BENOCS)
  const isMultiTenancyEnabled = useFeatureFlag(FLAGS.MULTI_TENANCY)
  const isTicketsEnabled = useFeatureFlag(FLAGS.TICKETS)
  const isKnowledgeBaseEnabled = useFeatureFlag(FLAGS.KNOWLEDGE_BASE_PANEL)
  const insightsOpensExternally = useInsightsOpensExternally()

  const customerAsn = auth.user.company?.asn

  const baseResourceLinks: KnowledgeBaseAwareLink[] = useMemo(
    () => [
      {
        id: 'api',
        label: 'API',
        to: `${import.meta.env.VITE_API_DOCS_URL}`,
        external: true,
      },
      {
        id: 'knowledge-base',
        label: 'Knowledge Base',
        to: NAVIGATION_LINKS.KNOWLEDGE_BASE,
        external: true,
        knowledgeBaseKey: KNOWLEDGE_BASE_HOME,
      },
      {
        id: 'looking-glass',
        label: 'Looking Glass',
        to: NAVIGATION_LINKS.LOOKING_GLASS,
        external: true,
      },
      {
        id: 'bgp-communities',
        label: 'BGP Communities',
        to: NAVIGATION_LINKS.BGP_COMMUNITIES,
        external: true,
        knowledgeBaseKey: 'bgp-communities',
      },
      {
        id: 'terraform-provider',
        label: 'Terraform Provider',
        to: NAVIGATION_LINKS.TERRAFORM_PROVIDER,
        external: true,
      },
      {
        id: 'map',
        label: 'Network Map',
        to: LEGAL_LINKS.NETWORK_MAP,
        external: true,
        isSeparated: true,
      },
      {
        id: 'peeringdb-as5405',
        label: 'PeeringDB AS5405',
        to: NAVIGATION_LINKS.PEERINGDB_AS5405,
        external: true,
        isSeparated: true,
      },
      {
        id: 'bgp-tools-as5405',
        label: 'BGP.tools AS5405',
        to: NAVIGATION_LINKS.BGP_TOOLS_AS5405,
        external: true,
      },
      ...(customerAsn
        ? [
            {
              id: 'peeringdb-customer-as',
              label: `PeeringDB AS${customerAsn}`,
              to: `https://www.peeringdb.com/asn/${customerAsn}`,
              external: true,
            },
            {
              id: 'bgp-tools-customer-as',
              label: `BGP.tools AS${customerAsn}`,
              to: `https://bgp.tools/as/${customerAsn}`,
              external: true,
            },
          ]
        : []),
      {
        id: 'changelog',
        label: 'Changelog',
        to: NAVIGATION_LINKS.CHANGELOG,
        external: true,
        knowledgeBaseKey: 'release-notes',
        isSeparated: true,
      },
    ],
    [customerAsn]
  )

  const resourceLinks = useMemo(
    () => withKnowledgeBase(isKnowledgeBaseEnabled, baseResourceLinks),
    [isKnowledgeBaseEnabled, baseResourceLinks]
  )

  const items: MainNavItem[] = useMemo(
    () => [
      { id: 'dashboard', to: '/', title: 'Dashboard' },
      {
        id: 'customers',
        to: '/customers',
        title: 'Customers',
        // While a customer's portal is being viewed the nav must show what that
        // customer sees, and a reseller's customer has no customers of its own.
        // Leaving the portal still works: CustomerBanner links to /customers.
        isHidden:
          Boolean(customerId) ||
          !isMultiTenancyEnabled ||
          !auth.user.is_reseller,
      },
      ...(isBenocsEnabled
        ? [
            {
              id: 'insights',
              to: '/insights',
              title: 'Insights',
              external: insightsOpensExternally,
              showArrow: false,
            },
          ]
        : []),
      { id: 'billing', to: '/billing', title: 'Billing' },
      {
        id: 'tickets',
        to: '/tickets',
        title: 'Tickets',
        isHidden: !isTicketsEnabled,
      },
      {
        id: 'tools',
        title: 'Tools',
        links: resourceLinks,
        alignment: 'left',
      },
    ],
    [
      customerId,
      isMultiTenancyEnabled,
      isBenocsEnabled,
      insightsOpensExternally,
      isTicketsEnabled,
      auth.user.is_reseller,
      resourceLinks,
    ]
  )

  const settingsHref = auth.user.customer_id
    ? '/settings/users'
    : '/settings/create-customer'

  return { items, resourceLinks, settingsHref }
}
